// Academy home, docs-site style: a hero with the two entry actions, the
// "New since you joined" strip, the course grid, and the sample files
// section the navigator's quick link anchors to.
import { AcademicCapIcon, ArrowDownTrayIcon, MusicalNoteIcon, PlayIcon, SparklesIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import { createRevealSequence } from "../../components/common/Reveal";
import { downloadFile } from "../../components/common/DownloadCard";
import { useAuth } from "../../context/AuthContext";
import { ALL_LESSONS, COURSES, courseMinutes, getCourse, newLessonsSince } from "../catalog";
import { useAcademyProgress } from "../progress";
import { SAMPLES_BASE } from "../scenes/types";
import { Chip } from "./Chip";
import CourseCard from "./CourseCard";
import LessonCard, { lessonPath } from "./LessonCard";
import { firstLesson } from "./AcademyNavigator";

const NEW_STRIP_MAX = 6;

const AcademyHome: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const createdAt = (user as any)?.created_at as string | undefined;
  const { progress } = useAcademyProgress();
  const newLessons = useMemo(() => newLessonsSince(createdAt).filter((l) => !progress.viewed?.[l.id]).slice(0, NEW_STRIP_MAX), [createdAt, progress.viewed]);
  const samples = useMemo(() => ALL_LESSONS.filter((l) => l.sample), []);
  const totalMinutes = COURSES.reduce((n, c) => n + courseMinutes(c.id), 0);
  const start = firstLesson();

  // #whats-new / #samples from the navigator's quick links.
  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, location.key]);

  const enter = createRevealSequence();
  const slot = (extra?: string) => {
    const r = enter();
    return { className: cn(r.className, extra), style: r.style };
  };

  return (
    <div className="space-y-10">
      {/* Hero */}
      <header {...slot("max-w-3xl")}>
        <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 px-3 py-1 text-xs font-medium">
          <AcademicCapIcon className="size-4" /> Academy
        </div>
        <h1 className="text-3xl font-semibold mt-3">Police Narratives AI Academy</h1>
        <p className="text-base-content/70 text-base mt-2 m-0">Short lessons on every part of Police Narratives AI. Each one shows the real app, and most can walk you through it live in your own account.</p>
        <p className="text-base-content/50 text-sm mt-2 m-0">
          {COURSES.length} courses · {ALL_LESSONS.length} lessons · {totalMinutes} min total
        </p>
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          {start && (
            <Button variant="primary" className="px-4" onClick={() => navigate(lessonPath(start))}>
              <PlayIcon className="size-5" /> Start here: {start.title}
            </Button>
          )}
          <Button variant="secondary" className="px-4" onClick={() => document.getElementById(newLessons.length ? "whats-new" : "courses")?.scrollIntoView({ behavior: "smooth", block: "start" })}>
            <SparklesIcon className="size-5" /> What's new
          </Button>
        </div>
      </header>

      {/* New since you joined */}
      {newLessons.length > 0 && (
        <section id="whats-new" {...slot("scroll-mt-24")}>
          <div className="flex items-center gap-2 mb-3">
            <SparklesIcon className="h-4 w-4 text-blue-500" />
            <h2 className="m-0 text-sm font-semibold text-gray-600 dark:text-base-content/70 uppercase tracking-wider">New since you joined</h2>
            <Chip tone="blue">{newLessons.length}</Chip>
          </div>
          <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1 snap-x">
            {newLessons.map((l) => (
              <div key={l.id} className="w-72 shrink-0 snap-start">
                <LessonCard lesson={l} showCourse className="h-full" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Courses */}
      <section id="courses" className="scroll-mt-24">
        <div {...slot("flex items-center gap-2 mb-3")}>
          <h2 className="m-0 text-sm font-semibold text-gray-600 dark:text-base-content/70 uppercase tracking-wider">Courses</h2>
        </div>
        {COURSES.length === 0 ? (
          <div className="rounded-lg border border-dashed border-base-300 bg-base-100 p-12 text-center">
            <AcademicCapIcon className="h-10 w-10 mx-auto text-base-content/30 mb-3" />
            <p className="font-medium text-base-content">Lessons are on the way</p>
            <p className="text-sm text-base-content/60">The Academy is being filled in. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {COURSES.map((c) => (
              <div key={c.id} {...slot()}>
                <CourseCard course={c} className="h-full" />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Sample files */}
      {samples.length > 0 && (
        <section id="samples" {...slot("scroll-mt-24")}>
          <div className="flex items-center gap-2 mb-1">
            <MusicalNoteIcon className="h-4 w-4 text-base-content/50" />
            <h2 className="m-0 text-sm font-semibold text-gray-600 dark:text-base-content/70 uppercase tracking-wider">Sample files</h2>
          </div>
          <p className="m-0 mb-3 text-sm text-base-content/60">Fictional recordings and documents you can run through the app to see what it does. Each lesson explains what to expect.</p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 m-0 p-0 list-none">
            {samples.map((l) => (
              <li key={l.id} className="flex items-center gap-3 rounded-lg border border-base-300 bg-base-100 p-3">
                <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                  <MusicalNoteIcon className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="m-0 text-sm font-medium truncate">{l.sample!.title}</p>
                  <p className="m-0 text-xs text-base-content/60 truncate">
                    {l.sample!.length} · used in{" "}
                    <button type="button" className="underline-offset-2 hover:underline text-blue-600 dark:text-blue-300" onClick={() => navigate(lessonPath(l))}>
                      {l.title}
                    </button>
                    {getCourse(l.courseId) ? ` (${getCourse(l.courseId)!.title})` : ""}
                  </p>
                </div>
                <Button variant="secondary" size="sm" className="px-3 shrink-0" onClick={() => downloadFile(`${SAMPLES_BASE}/${l.sample!.file}`, l.sample!.file)}>
                  <ArrowDownTrayIcon className="size-4" /> Download
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default AcademyHome;
